import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '../base'
import { AppButton } from '../AppButton'
import { AppIcons } from '../AppIcons'
import { useTheme } from '@/hooks/useTheme'

interface PhotoInputProps {
  centralize?: boolean
  size?: number
  sizeButtonIcon?: number
  iconSize?: number
  uri?: string
  onSelectImage?: (uri: string) => void
  isLoading?: boolean
}

export const PhotoInput: React.FC<PhotoInputProps> = ({
  centralize = false,
  size = 132,
  sizeButtonIcon = 40,
  iconSize = 18,
  uri,
  onSelectImage,
  isLoading = false,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const responsiveIconContainer = sizeButtonIcon + 8
  const responsiveIconCircle = sizeButtonIcon

  const defaultImageStyle = {
    backgroundColor: theme.colors.background.textInput,
    width: size,
    height: size,
    borderRadius: size / 2,
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && onSelectImage) {
      // Создаем URL для предварительного просмотра
      const imageUrl = URL.createObjectURL(file)
      onSelectImage(imageUrl)
    }
  }

  const pickImage = () => {
    if (!isLoading && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <BaseView
      style={{
        ...(centralize ? { alignItems: 'center' } : {}),
        ...(size
          ? {
              height: size,
              width: '100%',
            }
          : {}),
      }}
    >
      {/* Скрытый input для выбора файла */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <BaseView
        style={{
          position: 'relative',
          cursor: !isLoading ? 'pointer' : 'default',
        }}
        onClick={pickImage}
      >
        <BaseView
          style={{
            ...defaultImageStyle,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          {uri ? (
            <img
              src={uri}
              alt="Profile"
              crossOrigin="anonymous"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              onError={(e) => {
                console.error('Failed to load image:', uri)
                // Если не загрузилось, попробуем без crossOrigin
                const target = e.target as HTMLImageElement
                if (target.crossOrigin) {
                  target.crossOrigin = null
                  target.src = uri
                }
              }}
            />
          ) : (
            <BaseText
              fontSize={16}
              color="label.default"
              style={{ textAlign: 'center' }}
            >
              {t('translation:signup.addPhoto')}
            </BaseText>
          )}

          {/* Иконка редактирования */}
          <BaseView
            style={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              height: responsiveIconContainer,
              width: responsiveIconContainer,
              borderRadius: responsiveIconContainer / 2,
              backgroundColor: theme.colors.background.screen,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: responsiveIconCircle,
                height: responsiveIconCircle,
                borderRadius: '50%',
                background:
                  'linear-gradient(135deg, rgb(120, 102, 255), rgb(89, 135, 255))',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                border: 'none',
                outline: 'none',
              }}
              onClick={(e) => {
                e?.stopPropagation?.()
                pickImage()
              }}
            >
              {isLoading ? (
                <BaseText color="white" fontSize={12}>
                  ...
                </BaseText>
              ) : (
                <AppIcons
                  name={uri ? 'edit' : 'plusCircle'}
                  size={30}
                  color="white"
                />
              )}
            </div>
          </BaseView>
        </BaseView>
      </BaseView>
    </BaseView>
  )
}
